"use client";

import { useEffect, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "@/app/providers/theme-provider";

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function ThemeToggle() {
  const mounted = useMounted();
  const { resolvedTheme, setTheme } = useTheme();
  const next = resolvedTheme === "dark" ? "light" : "dark";
  const label = mounted ? `Switch to ${next} theme` : "Change color theme";
  return (
    <button
      type="button"
      data-theme-toggle
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted hover:bg-surface-hover hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Sun className="hidden h-[18px] w-[18px] dark:block" aria-hidden="true" />
      <Moon className="h-[18px] w-[18px] dark:hidden" aria-hidden="true" />
    </button>
  );
}

const options = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
] as const;

export function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const mounted = useMounted();
  const selected = mounted ? theme : "system";
  return (
    <fieldset className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
      <legend className="sr-only">Appearance</legend>
      <span className="text-xs font-semibold text-muted" aria-hidden="true">Appearance</span>
      <div className="inline-flex gap-0.5 rounded-full border border-line bg-surface p-1">
        {options.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            aria-pressed={selected === value}
            onClick={() => setTheme(value)}
            className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${selected === value ? "bg-surface-raised text-ink shadow-sm" : "text-muted hover:text-ink"}`}
          >
            <Icon size={14} aria-hidden="true" />{label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
